// Copyright © 2023 The Things Network Foundation, The Things Industries B.V.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useCallback, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { APPLICATION } from '@console/constants/entities'

import IconTTS from '@ttn-lw/components/icon/supplemental/tts'
import {
  IconChartHistogram,
  IconStar,
  IconLogout,
  IconUserCircle,
  IconBook,
  IconAdminPanel,
  IconApplication,
  IconDevice,
  IconGateway,
  IconOrganization,
  IconSupport,
} from '@ttn-lw/components/icon'
import HeaderComponent from '@ttn-lw/components/header'
import Dropdown from '@ttn-lw/components/dropdown'

import {
  selectNocUrl,
  selectPluginTTSCloud,
  selectPluginTTSCloudEnabled,
  selectBrandingRootPath,
  selectAssetsRootPath,
  selectPluginTTSSandboxEnabled,
  selectTTNLogo,
  selectBrandingDropdownLogo,
} from '@ttn-lw/lib/selectors/env'
import PropTypes from '@ttn-lw/lib/prop-types'
import sharedMessages from '@ttn-lw/lib/shared-messages'

import {
  checkFromState,
  mayCreateEntities,
  mayCreateApplications,
  mayCreateGateways,
  mayCreateOrganizations,
  mayCreateDevices,
  mayViewLabels,
} from '@console/lib/feature-checks'

import { logout } from '@console/store/actions/logout'
import { setSearchOpen, setSearchScope } from '@console/store/actions/search'

import { selectUser, selectUserIsAdmin } from '@console/store/selectors/user'
import { selectOwnTenant } from '@console/store/selectors/tenant.tti'
import { selectTotalUnseenCount } from '@console/store/selectors/notifications'
import { selectConsolePreferences } from '@console/store/selectors/user-preferences'

import Logo from '../logo'
import SidebarContext from '../sidebar/context'

import NotificationsDropdown from './notifications-dropdown'
import BookmarksDropdown from './bookmarks-dropdown'
import LabelsDropdown from './labels-dropdown.tti'

// TTI only.
const nocUrl = selectNocUrl()
const ttsCloudPluginEnabled = selectPluginTTSCloudEnabled()
const smUrl = selectPluginTTSCloud().subscription_management_url
const plansPageUrl = selectPluginTTSCloud().plans_page_url
// End TTI.

const Header = ({ alwaysShowLogo }) => {
  const dispatch = useDispatch()
  const {
    isMinimized,
    onMinimizeToggle,
    isDrawerOpen,
    openDrawer,
    closeDrawer,
    setIsHovered: setIsSideBarHovered,
  } = useContext(SidebarContext)

  const handleLogout = useCallback(() => dispatch(logout()), [dispatch])
  const user = useSelector(selectUser)
  const mayCreateApps = useSelector(state =>
    user ? checkFromState(mayCreateApplications, state) : false,
  )
  const mayCreateGtws = useSelector(state =>
    user ? checkFromState(mayCreateGateways, state) : false,
  )
  const mayCreateOrgs = useSelector(state =>
    user ? checkFromState(mayCreateOrganizations, state) : false,
  )
  const mayCreateDev = useSelector(state =>
    user ? checkFromState(mayCreateDevices, state) : false,
  )
  const hasCreateRights = useSelector(state => user && checkFromState(mayCreateEntities, state))
  const mayViewLbls = useSelector(state => user && checkFromState(mayViewLabels, state))
  const isAdmin = useSelector(selectUserIsAdmin)
  const hasUnseenNotifications = useSelector(selectTotalUnseenCount) > 0

  // TTI only.
  const showNOCButton = Boolean(nocUrl) && isAdmin
  const showSMButton = ttsCloudPluginEnabled && isAdmin
  const ownTenant = useSelector(selectOwnTenant)
  const tenantStripe = ownTenant?.billing?.stripe
  const subscriptionId = tenantStripe?.subscription_id
  let upgradeUrl = ''
  let manageSubscriptionsUrl = ''
  if (subscriptionId) {
    manageSubscriptionsUrl = `${smUrl}/dashboard/subscriptions/${subscriptionId}`
    upgradeUrl = `${smUrl}/dashboard/subscriptions/${subscriptionId}/upgrade-cloud`
  } else {
    manageSubscriptionsUrl = `${smUrl}/dashboard/subscriptions`
    upgradeUrl = plansPageUrl
  }
  const consolePreferences = useSelector(selectConsolePreferences)
  const dark =
    consolePreferences.console_theme === 'CONSOLE_THEME_DARK' ||
    (consolePreferences.console_theme === 'CONSOLE_THEME_SYSTEM' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches)
  // End TTI.
  const onDrawerExpandClick = useCallback(() => {
    if (!isDrawerOpen) {
      openDrawer()
    } else {
      closeDrawer()
    }
  }, [isDrawerOpen, openDrawer, closeDrawer])

  const handleRegisterEndDeviceClick = useCallback(() => {
    dispatch(setSearchScope(APPLICATION))
    dispatch(setSearchOpen(true))
  }, [dispatch])

  const plusDropdownItems = (
    <>
      {mayCreateApps && (
        <Dropdown.Item
          title={sharedMessages.addApplication}
          icon={IconApplication}
          path="/applications/add"
        />
      )}
      {mayCreateGtws && (
        <Dropdown.Item title={sharedMessages.addGateway} icon={IconGateway} path="/gateways/add" />
      )}
      {mayCreateOrgs && (
        <Dropdown.Item
          title={sharedMessages.addOrganization}
          icon={IconOrganization}
          path="/organizations/add"
        />
      )}
      {mayCreateDev && (
        <Dropdown.Item
          title={sharedMessages.registerDeviceInApplication}
          icon={IconDevice}
          action={handleRegisterEndDeviceClick}
        />
      )}
    </>
  )

  const dropdownItems = (
    <React.Fragment>
      <Dropdown.Item
        title={sharedMessages.profileSettings}
        icon={IconUserCircle}
        path="/user-settings/profile"
      />

      {/* TTI only. */}
      {showSMButton && (
        <Dropdown.Item
          title={sharedMessages.manageCloudSubscription}
          icon={IconTTS}
          path={manageSubscriptionsUrl}
          external
        />
      )}
      {showNOCButton && (
        <Dropdown.Item
          title={sharedMessages.noc}
          icon={IconChartHistogram}
          path={nocUrl}
          external
        />
      )}
      {/* End TTI. */}
      {isAdmin && (
        <Dropdown.Item
          title={sharedMessages.adminPanel}
          icon={IconAdminPanel}
          path="/admin-panel/network-information"
        />
      )}
      <hr />
      {/* TTI only. */}
      {showSMButton && (
        <Dropdown.Item title={sharedMessages.upgrade} icon={IconStar} path={upgradeUrl} external />
      )}
      {/* End TTI. */}
      <Dropdown.Item
        title={sharedMessages.getSupport}
        icon={IconSupport}
        path="https://thethingsindustries.com/support"
        external
      />
      <Dropdown.Item
        title={sharedMessages.documentation}
        icon={IconBook}
        path="https://thethingsindustries.com/docs"
        external
      />
      <hr />
      <Dropdown.Item title={sharedMessages.logout} icon={IconLogout} action={handleLogout} />
    </React.Fragment>
  )

  // TTI only.
  const hasCustomBranding = selectBrandingRootPath() !== selectAssetsRootPath()
  const brandLogo = selectPluginTTSSandboxEnabled()
    ? {
        src: selectTTNLogo(dark),
        alt: 'TTN Logo',
      }
    : hasCustomBranding
      ? {
          src: `${selectBrandingDropdownLogo()}`,
          alt: 'Logo',
        }
      : undefined
  // End TTI.

  const handleExpandSidebar = useCallback(() => {
    onDrawerExpandClick()
    setIsSideBarHovered(true)
  }, [onDrawerExpandClick, setIsSideBarHovered])

  const handleHideSidebar = useCallback(() => {
    setIsSideBarHovered(false)
  }, [setIsSideBarHovered])

  return (
    <HeaderComponent
      isSidebarMinimized={isMinimized}
      toggleSidebarMinimized={onMinimizeToggle}
      user={user}
      profileDropdownItems={dropdownItems}
      addDropdownItems={plusDropdownItems}
      bookmarkDropdownItems={<BookmarksDropdown />}
      notificationsDropdownItems={<NotificationsDropdown />}
      labelsDropdownItems={mayViewLbls ? <LabelsDropdown /> : undefined}
      brandLogo={brandLogo}
      Logo={Logo}
      onMenuClick={onDrawerExpandClick}
      showNotificationDot={hasUnseenNotifications}
      alwaysShowLogo={alwaysShowLogo}
      expandSidebar={handleExpandSidebar}
      handleHideSidebar={handleHideSidebar}
      hasCreateRights={hasCreateRights}
    />
  )
}

Header.propTypes = {
  alwaysShowLogo: PropTypes.bool,
}

Header.defaultProps = {
  alwaysShowLogo: false,
}

export default Header
